import { DateTime } from 'luxon';
import { useFrontendTool } from '@copilotkit/react-core';
import { Card, Skeleton, Stack, Text, Title } from '@mantine/core';

import { TrackableStatusWidget as TrackableStatusWidgetType } from 'types/dashboard_widget';

import { useModal } from 'hooks/useModal';
import { useTrackableLists, useWatchlistStatus } from 'trackables/hooks';
import { resolveTrackableSourceDetails } from 'trackables/utils';

import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Icon } from 'components/Icon/IconDeprecated';

import { AddToDashboardMenu } from './AddToDashboardMenu';

type ShowWatchlistStatusArgs = {
  watchlistIds?: string;
  watchlistItemIds?: string;
  timeRangeStart?: string;
  timeRangeStop?: string;
};

type ShowWatchlistStatusRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: ShowWatchlistStatusArgs;
};

function safeJsonParse<T>(json: string | T | undefined, fallback: T): T {
  if (!json) return fallback;
  if (typeof json !== 'string') return json;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function WatchlistStatusRenderer({ status, args }: ShowWatchlistStatusRenderProps) {
  const { open } = useModal();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    watchlistIds: watchlistIdsJson,
    watchlistItemIds: watchlistItemIdsJson,
    timeRangeStart,
    timeRangeStop,
  } = args;

  const watchlistIds: string[] = safeJsonParse(watchlistIdsJson, []);
  const selectedWatchlistItemIds: string[] = safeJsonParse(watchlistItemIdsJson, []);

  // Default time range: last 24 hours
  const now = new Date();
  const defaultStart = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const defaultStop = now.toISOString();
  const start = timeRangeStart || defaultStart;
  const stop = timeRangeStop || defaultStop;

  const {
    trackableItems,
    matchesByTrackableId,
    streamsMap,
    camerasMap,
    filesMap,
    isLoading: isDataLoading,
    shouldFetchItems,
  } = useWatchlistStatus({
    watchlistIds,
    watchlistItemIds: selectedWatchlistItemIds,
    timeRangeStart: start,
    timeRangeStop: stop,
    enabled: status === 'complete',
  });

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 4, h: 4 };

    return new TrackableStatusWidgetType(
      crypto.randomUUID(),
      'Watchlist Status',
      {
        trackable_list_ids: watchlistIds.length > 0 ? watchlistIds : undefined,
        trackable_item_ids: selectedWatchlistItemIds.length > 0 ? selectedWatchlistItemIds : undefined,
      },
      defaultLayout
    );
  };

  const isLoading = status !== 'complete' || isDataLoading;

  if (isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text c="dimmed" size="sm">
            Loading watchlist status...
          </Text>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton height={48} width={48} radius="md" />
              <div className="flex-1">
                <Skeleton height={16} width="60%" mb={8} />
                <Skeleton height={12} width="40%" />
              </div>
            </div>
          ))}
        </Stack>
      </Card>
    );
  }

  if (!shouldFetchItems) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          Please specify watchlist IDs or watchlist item IDs to monitor.
        </Text>
      </Card>
    );
  }

  if (trackableItems.length === 0) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No watchlist items found in the selected lists.
        </Text>
      </Card>
    );
  }

  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setZone(timezone).toLocaleString(DateTime.DATETIME_MED);

  return (
    <Card withBorder p="md" radius="md" className="relative">
      <Stack gap="sm">
        <Title order={5}>Watchlist Status</Title>
        <Text size="xs" c="dimmed">
          {formatDateTime(start)} → {formatDateTime(stop)}
        </Text>

        <div className="flex flex-col gap-2">
          {trackableItems.map((item) => {
            const match = matchesByTrackableId.get(item.id);
            const handleClick = match
              ? () =>
                  open('files-search', {
                    defaultFile: {
                      id: match.file_id,
                      created_at: match.created_at,
                    },
                    defaultStreamId: match.stream_id,
                  })
              : undefined;

            const { sourceLabel, sourceIcon } =
              resolveTrackableSourceDetails(match, {
                cameras: camerasMap,
                streams: streamsMap,
                files: filesMap,
              });
            const showSource = sourceLabel && sourceIcon !== 'file';
            const lastMatchLabel = match
              ? DateTime.fromISO(match.created_at)
                  .setZone(timezone)
                  .toLocaleString(DateTime.DATETIME_MED)
              : null;

            return (
              <button
                key={item.id}
                type="button"
                onClick={handleClick}
                disabled={!match}
                className={`flex items-center gap-3 rounded-md border p-2 text-left transition-colors ${
                  match
                    ? 'cursor-pointer border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:border-neutral-500 dark:hover:bg-neutral-800'
                    : 'cursor-default border-neutral-100 opacity-60 dark:border-neutral-800'
                }`}
              >
                {match ? (
                  <div className="h-12 w-12 shrink-0">
                    <Thumbnail
                      className="rounded"
                      mime_type={match.mime_type}
                      thumbnail_url={match.thumbnail_url}
                    />
                  </div>
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-neutral-100 dark:bg-neutral-800">
                    <Icon name="person" size="small" className="text-neutral-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {item.name}
                  </Text>
                  {match ? (
                    <>
                      {lastMatchLabel && (
                        <Text size="xs" c="dimmed">
                          Last match: {lastMatchLabel}
                        </Text>
                      )}
                      {showSource && (
                        <Text size="xs" c="dimmed" className="flex items-center gap-1">
                          {sourceIcon && <Icon name={sourceIcon} size="xsmall" />}
                          <span>{sourceLabel}</span>
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text size="xs" c="dimmed">
                      No matches in selected time range
                    </Text>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Stack>
      <div className="absolute bottom-2 right-2">
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useShowWatchlistStatusTool() {
  const now = new Date().toISOString();

  // Fetch available watchlists for the description
  const { data: watchlistsData } = useTrackableLists({
    params: { limit: 50 },
  });

  const watchlistInfo = watchlistsData?.data?.length
    ? `Available watchlists: ${watchlistsData.data.map(w => `"${w.name}" (${w.id})`).join(', ')}.`
    : '';

  useFrontendTool(
    {
      name: 'showWatchlistStatus',
      description: `Display the status of watchlist items, showing the latest match for each item within a time range. Watchlists are used to track specific faces, vehicles, or custom objects. ${watchlistInfo} Current date/time: ${now}.`,
      parameters: [
        {
          name: 'watchlistIds',
          type: 'string',
          description:
            'JSON array of watchlist (trackable list) IDs to monitor. Shows all items from these lists.',
          required: false,
        },
        {
          name: 'watchlistItemIds',
          type: 'string',
          description:
            'JSON array of specific watchlist item IDs to monitor. Use this to show only specific items.',
          required: false,
        },
        {
          name: 'timeRangeStart',
          type: 'string',
          description:
            'Start of time range in ISO 8601 format. Defaults to 24 hours ago.',
          required: false,
        },
        {
          name: 'timeRangeStop',
          type: 'string',
          description:
            'End of time range in ISO 8601 format. Defaults to now.',
          required: false,
        },
      ],
      handler: async (args: ShowWatchlistStatusArgs) => {
        return { ...args, message: 'Watchlist status displayed to user' };
      },
      render: ({ status, args }) => (
        <WatchlistStatusRenderer status={status} args={args as ShowWatchlistStatusArgs} />
      ),
    },
    [watchlistInfo]
  );
}
